import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import RegisterForm from './components/RegisterForm';
import FighterList from './components/FighterList';

function App() {
    const [mode, setMode] = useState('loading'); // loading | public | telegram
    const [user, setUser] = useState(null);
    const [events, setEvents] = useState([]);
    const [myRegistrations, setMyRegistrations] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [error, setError] = useState('');

    const tg = window.Telegram?.WebApp || null;

    const featuredEvent = useMemo(() => {
        return events[0] || null;
    }, [events]);

    const selectedEvent = useMemo(() => {
        if (!events.length) return null;
        if (!selectedEventId) return events[0];
        return events.find((e) => String(e._id) === String(selectedEventId)) || events[0];
    }, [events, selectedEventId]);

    const selectedRegistration = useMemo(() => {
        if (!selectedEvent) return null;
        return myRegistrations.find((r) => String(r.eventId) === String(selectedEvent._id)) || null;
    }, [myRegistrations, selectedEvent]);

    const isAdmin = !!user?.roles?.includes('admin');

    async function apiFetch(url, options = {}) {
        const res = await fetch(url, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(options.headers || {})
            },
            ...options
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
            const err = new Error(data?.message || data?.error || 'Request failed');
            err.payload = data;
            throw err;
        }

        return data;
    }

    async function loadEvents() {
        try {
            const data = await apiFetch('/api/events', { method: 'GET' });
            const nextEvents = Array.isArray(data?.events) ? data.events : [];
            setEvents(nextEvents);

            if (nextEvents.length > 0 && !selectedEventId) {
                setSelectedEventId(nextEvents[0]._id);
            }
        } catch (err) {
            console.error('Failed to load events:', err);
            setEvents([]);
        }
    }

    async function loadMyRegistrations() {
        try {
            const data = await apiFetch('/api/registrations/me', { method: 'GET' });
            setMyRegistrations(Array.isArray(data?.registrations) ? data.registrations : []);
        } catch (err) {
            console.error('Failed to load registrations:', err);
            setMyRegistrations([]);
        }
    }

    useEffect(() => {
        let active = true;

        async function boot() {
            try {
                await loadEvents();

                if (tg) {
                    try { tg.ready(); } catch (_) {}
                    try { tg.expand(); } catch (_) {}

                    const initData = tg.initData;

                    if (initData) {
                        const data = await apiFetch('/api/auth/telegram', {
                            method: 'POST',
                            body: JSON.stringify({ initData })
                        });

                        if (!active) return;

                        setUser(data?.user || null);
                        setMode('telegram');

                        if (data?.user) {
                            await loadMyRegistrations();
                        }
                    } else {
                        if (active) setMode('telegram');
                    }
                } else {
                    if (active) setMode('public');
                }
            } catch (err) {
                console.error('Boot failed:', err);
                if (active) {
                    setError('Unable to initialize Y Fight.');
                    setMode(tg ? 'telegram' : 'public');
                }
            }
        }

        boot();

        return () => {
            active = false;
        };
    }, []);

    async function handleRegistrationSuccess() {
        await loadMyRegistrations();
        await loadEvents();

        if (tg?.HapticFeedback) {
            try {
                tg.HapticFeedback.notificationOccurred('success');
            } catch (_) {}
        }
    }

    if (mode === 'loading') {
        return <div className="loader">LOADING...</div>;
    }

    return (
        <div className="App">
            <div className="top-nav">
                <div className="brand-main">Y FIGHT</div>
            </div>

            {mode === 'public' && (
                <PublicView featuredEvent={featuredEvent} events={events} />
            )}

            {mode === 'telegram' && (
                <TelegramView
                    user={user}
                    isAdmin={isAdmin}
                    events={events}
                    selectedEvent={selectedEvent}
                    selectedEventId={selectedEventId}
                    setSelectedEventId={setSelectedEventId}
                    selectedRegistration={selectedRegistration}
                    onRegistrationSuccess={handleRegistrationSuccess}
                    error={error}
                />
            )}
        </div>
    );
}

function PublicView({ featuredEvent, events }) {
    return (
        <div className="container">
            <h1>Y FIGHT</h1>
            <div className="tagline">
                Telegram-first multi-event fight platform
            </div>

            {featuredEvent && (
                <div className="featured-event">
                    <h2>{featuredEvent.title}</h2>

                    <div className="event-meta">
                        <div>
                            <span>📅</span> {new Date(featuredEvent.startsAt).toLocaleDateString()}
                        </div>
                        <div>
                            <span>📍</span> {featuredEvent.venue?.name || 'Venue TBA'}
                        </div>
                        <div>
                            <span>🧭</span> {featuredEvent.venue?.address || 'Address TBA'}
                        </div>
                        <div>
                            <span>👥</span> {featuredEvent.registrationCount || 0} / {featuredEvent.capacity}
                        </div>
                    </div>

                    <a
                        href="https://t.me/y_fight_bot"
                        className="cta-main"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Register via Telegram
                    </a>
                </div>
            )}

            {events.length > 1 && (
                <div className="event-list">
                    <h3>Other Upcoming Events</h3>
                    {events.slice(1).map((e) => (
                        <div key={e._id} className="event-card">
                            <div style={{ fontWeight: 'bold' }}>{e.title}</div>
                            <div className="dim">
                                {new Date(e.startsAt).toLocaleDateString()}
                            </div>
                            <div className="dim">
                                {e.venue?.name || 'Venue TBA'}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="action-row">
                <div className="qr-small">
                    <img src="/qr-link.jpeg" alt="Community Link" />
                </div>
                <div className="cta-text">
                    <p>Official Channel</p>
                    <a href="https://t.me/y_fight" className="btn-link">
                        Join Community
                    </a>
                </div>
            </div>

            <div className="footer">
                Powered by <a href="https://ensopeople.org">Enso People</a>
            </div>
        </div>
    );
}

function TelegramView({
    user,
    isAdmin,
    events,
    selectedEvent,
    selectedEventId,
    setSelectedEventId,
    selectedRegistration,
    onRegistrationSuccess,
    error
}) {
    return (
        <div className="container">
            <div className="welcome-box">
                WELCOME, {user?.profile?.displayName || user?.telegram?.firstName || 'FIGHTER'}
            </div>

            {error && (
                <div style={{ color: '#ff6b6b', marginTop: '1rem' }}>
                    {error}
                </div>
            )}

            {isAdmin ? (
                <FighterList />
            ) : (
                <>
                    {events.length > 0 && (
                        <div className="action-row" style={{ marginBottom: '1rem' }}>
                            <div className="cta-text" style={{ width: '100%' }}>
                                <p>Select Event</p>
                                <select
                                    value={selectedEventId || selectedEvent?._id || ''}
                                    onChange={(e) => setSelectedEventId(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.8rem',
                                        background: '#111',
                                        color: '#fff',
                                        border: '1px solid #333'
                                    }}
                                >
                                    {events.map((event) => (
                                        <option key={event._id} value={event._id}>
                                            {event.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <RegisterForm
                        event={selectedEvent}
                        existingRegistration={selectedRegistration}
                        onSignupSuccess={onRegistrationSuccess}
                    />
                </>
            )}
        </div>
    );
}

export default App;